import styles from './information-texts.module.scss';
import { useContext, useEffect, useState } from 'react';
import { Col, Row } from 'reactstrap';
import PageTitle from '../../page-title/page-title';
import { makeCourseRequest, useCourseGetRequestHook } from '../../requestUtils';
import { Box, Container, Froala, PrimaryButton, SecondaryButton, SnackBarContext, Tab, Tabs } from '@frontend-workspace/react-shared-lib';
import { ISetting } from '../../_models/setting';
import { useTranslation } from 'react-i18next';

/* eslint-disable-next-line */
export interface InformationTextsProps { }

export function InformationTexts(props: InformationTextsProps) {
  const { data: setting }: { data: ISetting } = useCourseGetRequestHook("settings", undefined);
  const { t } = useTranslation();
  const [selectedTab, setSelectedTab] = useState<number>(0);

  const navigateToTabOne = () => setSelectedTab(0);
  const navigateToTabTwo = () => setSelectedTab(1);
  const navigateToTabThree = () => setSelectedTab(2);

  return (
    <Container
      className="container-fluid pt-4 px-0"
      maxWidth={false}
      sx={{ paddingBottom: '50px' }}>
      <Row>
        <Col>
          <Box
            component="div"
            className="pb-5">
            <PageTitle title={t('app.informationTexts')} subtitle={`(${t("standard.admin")})`} />
            <Box sx={{ borderBottom: 1, borderColor: 'divider', marginBottom: '30px' }}>
              <Tabs
                value={selectedTab}
                variant="scrollable"
                scrollButtons="auto">
                <Tab label={t('informationTexts.courseSchedule')} onClick={navigateToTabOne} />
                <Tab label={t('informationTexts.generalInformation')} onClick={navigateToTabTwo} />
                <Tab label={t('informationTexts.practicalInformation')} onClick={navigateToTabThree} />
              </Tabs>
            </Box>
            <div>
              {selectedTab === 0 && setting &&
                <CourseSchedule
                  setting={setting}
                />}
              {selectedTab === 1 && setting &&
                <GeneralInformation
                  setting={setting}
                />}
              {selectedTab === 2 && setting &&
                <PracticalInformation
                  setting={setting}
                />}
            </div>
          </Box>
        </Col>
      </Row>
    </Container>
  );
}

function CourseSchedule(props: {
  setting: ISetting
}) {
  const { t } = useTranslation();
  const { setSnackbarMessage } = useContext(SnackBarContext);
  const [contentIngressOne, setContentIngressOne] = useState<string>('');
  const [contentIngressTwo, setContentIngressTwo] = useState<string>('');

  useEffect(() => {
    if (props.setting) {
      setContentIngressOne(props.setting?.firstpageInfo1Html);
      setContentIngressTwo(props.setting?.firstpageInfo2Html);
    }
  }, [props.setting]);

  const handleResetContentIngressOne = () => {
    const contentIngressOneCopy = props.setting.firstpageInfo1Html;
    setContentIngressOne(contentIngressOneCopy);
  };

  const handleResetContentIngressTwo = () => {
    const contentIngressTwoCopy = props.setting.firstpageInfo2Html;
    setContentIngressTwo(contentIngressTwoCopy);
  };

  const handleUpdateContentIngressOne = () => {
    makeCourseRequest(`settings/upsert`, 'POST', {
      id: props.setting.id,
      _ts: props.setting._ts,
      createdAt: props.setting.createdAt,
      coursesInfoHtml: props.setting.coursesInfoHtml,
      generalInfoHtml: props.setting.generalInfoHtml,
      firstpageInfo1Html: contentIngressOne,
      firstpageInfo2Html: props.setting.firstpageInfo2Html
    }).then(result => {
      setSnackbarMessage({ content: t('standard.updatedText'), type: "info", });
    }).catch(() => setSnackbarMessage({ content: t('standard.couldNotUpdateText'), type: "error" }));
  };

  // Status: 200 OK
  const handleUpdateContentIngressTwo = () => {
    makeCourseRequest(`settings/upsert`, 'POST', {
      id: props.setting.id,
      _ts: props.setting._ts,
      createdAt: props.setting.createdAt,
      coursesInfoHtml: props.setting.coursesInfoHtml,
      generalInfoHtml: props.setting.generalInfoHtml,
      firstpageInfo1Html: props.setting.firstpageInfo1Html,
      firstpageInfo2Html: contentIngressTwo
    }).then(result => {
      setSnackbarMessage({ content: t('standard.updatedText'), type: "info", });
    }).catch(() => setSnackbarMessage({ content: t('standard.couldNotUpdateText'), type: "error" }));
  };

  return (<>
    <Row>
      <Col xl={8}>
        <div className='mb-3'>
          <label className='mb-1'>{t('informationTexts.preamble')} 1</label>
          <Froala
            maxHeight={800}
            model={contentIngressOne || ''}
            onModelChanged={setContentIngressOne}
          />
        </div>
      </Col>
    </Row>
    <div className='mb-5'>
      <PrimaryButton
        variant="contained"
        onClick={handleUpdateContentIngressOne}
        className='minButtonWidth me-3'>
        {t('standard.save')}
      </PrimaryButton>
      <SecondaryButton
        variant='outlined'
        onClick={handleResetContentIngressOne}
        className='minButtonWidth'>
        {t('standard.reset')}
      </SecondaryButton>
    </div>

    <Row>
      <Col xl={8}>
        <div className='mb-3'>
          <label className='mb-1'>{t('informationTexts.preamble')} 2</label>
          <Froala
            maxHeight={800}
            model={contentIngressTwo || ''}
            onModelChanged={setContentIngressTwo}
          />
        </div>
      </Col>
    </Row>
    <div>
      <PrimaryButton
        variant="contained"
        onClick={handleUpdateContentIngressTwo}
        className='minButtonWidth me-3'>
        {t('standard.save')}
      </PrimaryButton>
      <SecondaryButton
        variant='outlined'
        onClick={handleResetContentIngressTwo}
        className='minButtonWidth'>
        {t('standard.reset')}
      </SecondaryButton>
    </div>
  </>);
}

function GeneralInformation(props: {
  setting: ISetting
}) {
  const [contentGeneral, setContentGeneral] = useState<string>('');
  const { setSnackbarMessage } = useContext(SnackBarContext);
  const { t } = useTranslation();

  const handleResetContentGeneral = () => {
    const contentGeneralCopy = props.setting.generalInfoHtml;
    setContentGeneral(contentGeneralCopy);
  };

  const handleUpdateContentGeneral = () => {
    makeCourseRequest(`settings/upsert`, 'POST', {
      id: props.setting.id,
      _ts: props.setting._ts,
      createdAt: props.setting.createdAt,
      coursesInfoHtml: props.setting?.coursesInfoHtml,
      generalInfoHtml: contentGeneral,
      firstpageInfo1Html: props.setting?.firstpageInfo1Html,
      firstpageInfo2Html: props.setting?.firstpageInfo2Html
    }).then(result => {
      setSnackbarMessage({ content: t('standard.updatedText'), type: "info", });
    }).catch(() => setSnackbarMessage({ content: t('standard.couldNotUpdateText'), type: "error" }));
  };

  useEffect(() => {
    if (props.setting) {
      setContentGeneral(props.setting?.generalInfoHtml);
    }
  }, [props.setting]);

  return (<>
    <Row>
      <Col xl={8}>
        <div className='mb-3'>
          <label className='mb-1'>{t('informationTexts.general')}</label>
          <Froala
            maxHeight={800}
            model={contentGeneral || ''}
            onModelChanged={setContentGeneral}
          />
        </div>
      </Col>
    </Row>
    <div>
      <PrimaryButton
        variant="contained"
        onClick={handleUpdateContentGeneral}
        className='minButtonWidth me-3'>
        {t('standard.save')}
      </PrimaryButton>
      <SecondaryButton
        variant='outlined'
        onClick={handleResetContentGeneral}
        className='minButtonWidth'>
        {t('standard.reset')}
      </SecondaryButton>
    </div>
  </>);
}

function PracticalInformation(props: {
  setting: ISetting
}) {
  const [contentPractical, setContentPractical] = useState<string>(props.setting.coursesInfoHtml);
  const { setSnackbarMessage } = useContext(SnackBarContext);
  const { t } = useTranslation();

  const handleResetContentPractical = () => {
    const contentPracticalCopy = props.setting.coursesInfoHtml;
    setContentPractical(contentPracticalCopy);
  }

  // Status: 200 OK
  const handleUpdateContentPractical = () => {
    makeCourseRequest(`settings/upsert`, 'POST', {
      id: props.setting.id,
      _ts: props.setting._ts,
      createdAt: props.setting.createdAt,
      coursesInfoHtml: contentPractical,
      generalInfoHtml: props.setting?.generalInfoHtml,
      firstpageInfo1Html: props.setting?.firstpageInfo1Html,
      firstpageInfo2Html: props.setting?.firstpageInfo2Html
    }).then(result => {
      setSnackbarMessage({ content: t('standard.updatedText'), type: "info", });
    }).catch(() => setSnackbarMessage({ content: t('standard.couldNotUpdateText'), type: "error" }));
  };

  useEffect(() => {
    if (props.setting) {
      setContentPractical(props.setting?.coursesInfoHtml);
    }
  }, [props.setting]);

  return (<>
    <Row>
      <Col xl={8}>
        <div className='mb-3'>
          <label className='mb-1'>{t('informationTexts.practical')}</label>
          <Froala
            maxHeight={800}
            model={contentPractical || ''}
            onModelChanged={setContentPractical}
          />
        </div>
      </Col>
    </Row>
    <div>
      <PrimaryButton
        variant="contained"
        onClick={handleUpdateContentPractical}
        className='minButtonWidth me-3'>
        {t('standard.save')}
      </PrimaryButton>
      <SecondaryButton
        variant='outlined'
        onClick={handleResetContentPractical}
        className='minButtonWidth'>
        {t('standard.reset')}
      </SecondaryButton>
    </div>
  </>);
}

export default InformationTexts;
